import { addHours, parseISO, isBefore, isAfter } from "date-fns";

type GetMaxDateParams = {
  maximumAdvanceBookingHours: number;
  endDate?: string | null;
};

type GetMinDateParams = {
  startDate?: string | null;
};

/**
 * Determines the latest possible date for a booking based on configured constraints.
 * @param maximumAdvanceBookingHours - The maximum number of hours into the future that a booking can be made
 *                                    (e.g., if set to 168, bookings can only be made up to 7 days in advance)
 * @param endDate - Optional end date for selective booking links
 * @returns The latest possible date for booking
 */
export const getLatestBookingDate = ({
  maximumAdvanceBookingHours,
  endDate,
}: GetMaxDateParams): Date => {
  const currentDate = new Date();
  const maxDateFromHours = addHours(currentDate, maximumAdvanceBookingHours);

  if (!endDate) {
    return maxDateFromHours;
  }

  const endDateTime = parseISO(endDate);
  return isBefore(endDateTime, maxDateFromHours)
    ? isBefore(endDateTime, currentDate)
      ? currentDate
      : endDateTime
    : maxDateFromHours;
};

/**
 * Determines the earliest possible date for a booking.
 * @param startDate - Optional start date for selective booking links
 * @returns The earliest possible date for booking
 */
export const getEarliestBookingDate = ({
  startDate,
}: GetMinDateParams): Date => {
  const currentDate = new Date();

  if (!startDate) {
    return currentDate;
  }

  const startDateTime = parseISO(startDate);
  return isAfter(startDateTime, currentDate) ? startDateTime : currentDate;
};
